/**
 * @file 양식 편집 화면의 선택지 — 칸 종류와 그 설명.
 *
 * 슬롯·연동은 서버 사전(/dictionary)에서 받는다. enum 이 늘어날 때 프론트를 같이 고쳐야 하는
 * 자리를 만들지 않기 위해서다. 칸 종류만 여기 둔다 — 렌더러(FieldRenderer)가 실제로 그릴 수 있는
 * 것만 골라야 하므로 프론트가 진실을 갖는 쪽이 맞다.
 */

/** FieldRenderer 가 그릴 수 있는 칸 종류. 여기 없는 값을 넣으면 상신 화면에서 일반 텍스트로 떨어진다. */
export const FIELD_TYPES = [
    { value: "TEXT",           label: "한 줄 입력",     hint: "짧은 문자" },
    { value: "TEXTAREA",       label: "여러 줄 입력",   hint: "사유·비고" },
    { value: "MONEY",          label: "금액",           hint: "천 단위 쉼표, 원 단위" },
    { value: "NUMBER",         label: "숫자",           hint: "수량·시간" },
    { value: "DATE",           label: "날짜",           hint: "" },
    { value: "TIME",           label: "시각",           hint: "" },
    { value: "EMAIL",          label: "이메일",         hint: "" },
    { value: "SELECT",         label: "드롭다운",       hint: "선택지 필요" },
    { value: "RADIO",          label: "라디오",         hint: "선택지 필요 · 하나만" },
    { value: "CHECKBOX",       label: "체크 하나",      hint: "예/아니오" },
    { value: "CHECKBOX_GROUP", label: "체크 여러 개",   hint: "선택지 필요" },
    { value: "SUBMITTER",      label: "기안자(자동)",   hint: "상신한 사람 이름이 자동으로 들어감" },
    { value: "SUBMITTER_DEPT", label: "기안자 부서(자동)", hint: "상신한 사람 부서가 자동으로 들어감" },
    { value: "EMPLOYEE",       label: "직원 선택",      hint: "" },
    { value: "EMPLOYEE_MULTI", label: "직원 여러 명",   hint: "" },
    { value: "DEPARTMENT",     label: "부서 선택",      hint: "" },
    { value: "VENDOR",         label: "거래처 선택",    hint: "직접 입력도 가능" },
    { value: "CHANNEL",        label: "채널 선택",      hint: "" },
    { value: "APPROVAL_REF",   label: "결재 문서 연결", hint: "품의 → 결의 잇기" },
    { value: "GRID",           label: "표(여러 행)",    hint: "열 정의 필요" },
    { value: "DERIVED",        label: "자동 계산",      hint: "입력 불가 · 다른 칸에서 계산" },
    { value: "HIDDEN",         label: "숨김",           hint: "화면에 안 보임" },
];

/** 선택지 목록이 필요한 칸. 비어 있으면 상신 화면에서 고를 게 없는 칸이 된다. */
export const NEEDS_OPTIONS = ["SELECT", "RADIO", "CHECKBOX_GROUP"];

export const typeLabel = (t) => FIELD_TYPES.find((x) => x.value === t)?.label || t;

export const STEP_RESOLVERS = [
    { value: "SUBMITTER",   label: "기안자",     hint: "상신한 본인. 결재자가 아니라 작성자로 표시된다." },
    { value: "TEAM_LEADER", label: "소속팀장",   hint: "기안자 부서의 팀장. 팀장이 없으면 이 단계는 건너뛴다." },
    { value: "FIXED",       label: "지정 인물",  hint: "항상 이 사람. 기안자 본인이면 건너뛴다." },
];

export const VIEWER_RESOLVERS = [
    { value: "FIXED",          label: "지정 인물", hint: "" },
    { value: "SUBMITTER_TEAM", label: "기안자 팀", hint: "기안자와 같은 부서 재직자 전원" },
];

/**
 * 칸 이름 → 저장 키.
 *
 * 키는 formData JSON 의 실제 열쇠라 한글을 그대로 쓰면 나중에 조회·백필이 지저분해진다.
 * 한글 라벨에서는 키를 못 만들므로 순번으로 떨어뜨린다 — 관리자가 직접 고칠 수도 있다.
 */
export function suggestKey(label, existingKeys) {
    const ascii = (label || "").trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    let base = ascii || "field";
    if (!existingKeys.includes(base)) return base;
    let n = 2;
    while (existingKeys.includes(`${base}${n}`)) n += 1;
    return `${base}${n}`;
}

/**
 * 슬롯 24개를 한 줄로 늘어놓으면 «지급예정일» 을 찾는 데도 목록을 끝까지 읽어야 한다.
 * 뜻으로 묶어 소제목을 달면 한 덩어리만 보면 된다.
 *
 * 여기 없는 슬롯(나중에 enum 이 늘어난 경우)은 «기타» 로 떨어진다 — 프론트를 안 고쳐도 안 사라진다.
 */
const SLOT_GROUPS = [
    { title: "금액",       codes: ["TOTAL_AMOUNT", "SUPPLY_AMOUNT", "VAT_AMOUNT"] },
    { title: "날짜·기간",   codes: ["PAYMENT_DUE_DATE", "PERIOD_START", "PERIOD_END", "WORK_DATE",
                                  "WORK_START_TIME", "WORK_END_TIME"] },
    { title: "거래처·프로젝트", codes: ["VENDOR_REF", "PROJECT_REF", "PAYEE_ACCOUNT", "BIZ_NO", "CARD_REF"] },
    { title: "휴가·근무",   codes: ["LEAVE_KIND", "LEAVE_HOURS", "COMPENSATION_KIND", "OVERTIME_MEAL"] },
    { title: "연결 문서",   codes: ["SUPERSEDES_REF", "RESTORE_TARGET"] },
    { title: "그 외",      codes: ["SETTLEMENT_KIND", "EXPENSE_PURPOSE", "ITEM_LINES", "REASON"] },
];

/** 서버 사전(slots)을 뜻별로 묶는다. @returns [{title, slots:[…]}] */
export function groupSlots(slots) {
    const bySlot = new Map((slots || []).map((s) => [s.code, s]));
    const used = new Set();
    const out = [];
    for (const g of SLOT_GROUPS) {
        const items = g.codes.map((c) => bySlot.get(c)).filter(Boolean);
        items.forEach((s) => used.add(s.code));
        if (items.length) out.push({ title: g.title, slots: items });
    }
    const rest = (slots || []).filter((s) => !used.has(s.code));
    if (rest.length) out.push({ title: "기타", slots: rest });
    return out;
}

/**
 * 칸 종류를 뜻으로 묶는다.
 *
 * 20개를 한 줄로 늘어놓으면 «금액» 하나 고르는 데도 목록을 훑어야 한다. 종류를 지우지는 않는다 —
 * 이미 그 종류로 발행된 양식이 있어서 지우면 그 칸이 일반 텍스트로 떨어진다.
 */
const TYPE_GROUPS = [
    { title: "기본 입력",  codes: ["TEXT", "TEXTAREA", "MONEY", "NUMBER", "DATE", "TIME"] },
    { title: "골라 넣기",  codes: ["SELECT", "RADIO", "CHECKBOX", "CHECKBOX_GROUP"] },
    { title: "자동으로 채워짐", codes: ["SUBMITTER", "SUBMITTER_DEPT"] },
    { title: "사람·조직·거래처", codes: ["EMPLOYEE", "EMPLOYEE_MULTI", "DEPARTMENT", "VENDOR", "CHANNEL"] },
    { title: "덜 쓰는 것",  codes: ["APPROVAL_REF", "GRID", "EMAIL", "DERIVED", "HIDDEN"] },
];

/** @returns [{title, types:[…]}] — 목록에 없는 새 종류는 «그 외» 로 떨어진다. */
export function groupFieldTypes() {
    const byCode = new Map(FIELD_TYPES.map((t) => [t.value, t]));
    const used = new Set();
    const out = [];
    for (const g of TYPE_GROUPS) {
        const items = g.codes.map((c) => byCode.get(c)).filter(Boolean);
        items.forEach((t) => used.add(t.value));
        if (items.length) out.push({ title: g.title, types: items });
    }
    const rest = FIELD_TYPES.filter((t) => !used.has(t.value));
    if (rest.length) out.push({ title: "그 외", types: rest });
    return out;
}
